import React, { useState } from "react";
import { globalWorkoutApi } from "../api/routes";
import { Button, Card, Input, Select, TextArea, Toast, TwoColumn } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const blankWorkout = {
  workoutId: "",
  version: 1,
  type: "",
  name: "",
  steps: [],
  notes: "",
  intensity: 0,
  tags: [],
};

const typeOptions = [
  "EASY_RUN",
  "TEMPO",
  "INTERVAL",
  "CROSS_TRAINING",
  "HILL",
  "TRAIL",
  "LONG_RUN",
  "RECOVERY",
  "STRENGTH",
  "OTHER",
];

const stepTypeOptions = ["WARMUP", "ACTIVE", "RECOVERY", "COOLDOWN", "REST"];
const durationTypeOptions = ["DISTANCE", "TIME", "OPEN", "LAP_BUTTON"];
const targetTypeOptions = ["PACE", "HR", "POWER", "CADENCE", "RPE", "NONE"];

const blankStep = {
  description: "",
  distanceKm: 0,
  paceTarget: "",
  type: "",
  stepType: "",
  durationType: "",
  durationValue: null,
  targetType: "",
  targetLow: "",
  targetHigh: "",
  repeat: false,
  repeatCount: null,
  repetitionSteps: [],
};

export default function GlobalWorkoutsPanel() {
  const [workout, setWorkout] = useState(blankWorkout);
  const [lookupId, setLookupId] = useState("");
  const [version, setVersion] = useState("");
  const [tag, setTag] = useState("");
  const [, setResponse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [searching, setSearching] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [steps, setSteps] = useState([]);
  const [panelState, setPanelState] = usePanelCache("globalWorkouts");
  const showSearch = panelState.showSearch ?? true;
  const results = panelState.results || [];

  const loadWorkout = (data = blankWorkout) => {
    const merged = { ...blankWorkout, ...data };
    setWorkout(merged);
    setTagInput("");
    setSteps(Array.isArray(merged.steps) ? merged.steps : []);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    setWorkout((w) => ({ ...w, tags: [...(w.tags || []), t] }));
    setTagInput("");
  };
  const removeTag = (tag) =>
    setWorkout((w) => ({ ...w, tags: (w.tags || []).filter((t) => t !== tag) }));

  const addStep = () =>
    setSteps((s) => [...s, { ...blankStep }]);
  const removeStep = (idx) => setSteps((s) => s.filter((_, i) => i !== idx));
  const updateStep = (idx, field, value) =>
    setSteps((s) => s.map((step, i) => (i === idx ? { ...step, [field]: value } : step)));
  const addSubStep = (idx) =>
    setSteps((s) =>
      s.map((step, i) =>
        i === idx
          ? { ...step, repetitionSteps: [...(step.repetitionSteps || []), { ...blankStep, repeat: false, repeatCount: null, repetitionSteps: [] }] }
          : step
      )
    );
  const updateSubStep = (idx, subIdx, field, value) =>
    setSteps((s) =>
      s.map((step, i) => {
        if (i !== idx) return step;
        const reps = [...(step.repetitionSteps || [])];
        reps[subIdx] = { ...reps[subIdx], [field]: value };
        return { ...step, repetitionSteps: reps };
      })
    );
  const removeSubStep = (idx, subIdx) =>
    setSteps((s) =>
      s.map((step, i) => {
        if (i !== idx) return step;
        const reps = [...(step.repetitionSteps || [])];
        reps.splice(subIdx, 1);
        return { ...step, repetitionSteps: reps };
      })
    );

  const normalizeStep = (step) => ({
    ...step,
    stepType: step.stepType || null,
    durationType: step.durationType || null,
    durationValue: step.durationValue === "" ? null : step.durationValue,
    targetType: step.targetType || null,
    targetLow: step.targetLow || null,
    targetHigh: step.targetHigh || null,
    repeatCount: step.repeatCount === "" ? null : step.repeatCount,
    repetitionSteps: (step.repetitionSteps || []).map(normalizeStep),
  });

  const buildPayload = () => ({
    ...workout,
    tags: workout.tags || [],
    steps: steps.map(normalizeStep),
  });

  const handleDelete = async (workoutId) => {
    if (!workoutId) return;
    setSearching(true);
    try {
      await globalWorkoutApi.delete(workoutId);
      setPanelState({ results: (results || []).filter((r) => r.workoutId !== workoutId) });
      setToast({ message: "Workout deleted", tone: "success" });
      setShowForm(false);
    } catch (e) {
      setResponse({ error: e.message });
      setToast({ message: e.message || "Error deleting workout", tone: "error" });
    } finally {
      setSearching(false);
    }
  };

  const createWorkout = async () => {
    try {
      const payload = buildPayload();
      const res = await globalWorkoutApi.create(payload);
      setResponse(res.data);
      setToast({ message: "Workout created", tone: "success" });
      setShowForm(false);
      setPanelState({ results: [res.data || payload, ...results] });
    } catch (e) {
      setResponse({ error: e.message });
      setToast({ message: e.message || "Error creating workout", tone: "error" });
    }
  };

  const updateWorkout = async () => {
    if (!workout.workoutId) return setResponse({ error: "workoutId required" });
    try {
      const payload = buildPayload();
      const res = await globalWorkoutApi.update(workout.workoutId, payload);
      setResponse(res.data);
      setToast({ message: "Workout updated", tone: "success" });
      setShowForm(false);
      setPanelState({
        results: results.map((r) => (r.workoutId === workout.workoutId ? { ...r, ...res.data } : r)),
      });
    } catch (e) {
      setResponse({ error: e.message });
      setToast({ message: e.message || "Error updating workout", tone: "error" });
    }
  };

  const fetchLatest = async () => {
    setSearching(true);
    try {
      const res = await globalWorkoutApi.latest(lookupId);
      loadWorkout(res.data);
      setResponse(res.data);
      setShowForm(true);
      setPanelState({ results: res.data ? [res.data] : [], showSearch: false });
    } catch (e) {
      setResponse({ error: e.message });
    } finally {
      setSearching(false);
    }
  };

  const fetchVersion = async () => {
    setSearching(true);
    try {
      const res = await globalWorkoutApi.version(lookupId, version);
      loadWorkout(res.data);
      setResponse(res.data);
      setShowForm(true);
      setPanelState({ results: res.data ? [res.data] : [], showSearch: false });
    } catch (e) {
      setResponse({ error: e.message });
    } finally {
      setSearching(false);
    }
  };

  const fetchByTag = async () => {
    setSearching(true);
    try {
      const res = await globalWorkoutApi.byTag(tag);
      setResponse(res.data);
      setPanelState({ results: Array.isArray(res.data) ? res.data : [], showSearch: false });
    } catch (e) {
      setResponse({ error: e.message });
    } finally {
      setSearching(false);
    }
  };

  const renderTagPills = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      {(workout.tags || []).map((tag) => (
        <span
          key={tag}
          style={{ background: "rgba(25,164,99,0.1)", padding: "6px 10px", borderRadius: 999, fontWeight: 700, display: "inline-flex", gap: 6, alignItems: "center" }}
        >
          {tag}
          <button
            onClick={() => removeTag(tag)}
            style={{ border: "none", background: "transparent", cursor: "pointer", fontWeight: 700, color: "#c0392b" }}
          >
            ✕
          </button>
        </span>
      ))}
    </div>
  );

  const renderSteps = () => (
    <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
      {steps.map((s, idx) => (
        <Card key={idx} title={`Step ${idx + 1}`} action={<Button tone="ghost" onClick={() => removeStep(idx)}>Remove</Button>}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8 }}>
            <Input label="Description" value={s.description || ""} onChange={(v) => updateStep(idx, "description", v)} />
            <Input label="Distance (km)" value={s.distanceKm} onChange={(v) => updateStep(idx, "distanceKm", Number(v) || 0)} />
            <Input label="Pace Target" value={s.paceTarget || ""} onChange={(v) => updateStep(idx, "paceTarget", v)} />
            <Input label="Type" value={s.type || ""} onChange={(v) => updateStep(idx, "type", v)} />
            <Select label="Step Type" value={s.stepType || ""} onChange={(v) => updateStep(idx, "stepType", v)} options={stepTypeOptions} />
            <Select label="Duration Type" value={s.durationType || ""} onChange={(v) => updateStep(idx, "durationType", v)} options={durationTypeOptions} />
            <Input label="Duration Value" value={s.durationValue ?? ""} onChange={(v) => updateStep(idx, "durationValue", v === "" ? null : Number(v) || 0)} />
            <Select label="Target Type" value={s.targetType || ""} onChange={(v) => updateStep(idx, "targetType", v)} options={targetTypeOptions} />
            <Input label="Target Low" value={s.targetLow || ""} onChange={(v) => updateStep(idx, "targetLow", v)} />
            <Input label="Target High" value={s.targetHigh || ""} onChange={(v) => updateStep(idx, "targetHigh", v)} />
            <Input label="Repeat Count" value={s.repeatCount ?? ""} onChange={(v) => updateStep(idx, "repeatCount", v === "" ? null : Number(v) || 0)} />
            <div>
              <label style={{ fontWeight: 600, marginBottom: 4, display: "block" }}>Repeat?</label>
              <input type="checkbox" checked={!!s.repeat} onChange={(e) => updateStep(idx, "repeat", e.target.checked)} />
            </div>
          </div>
          {s.repeat && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Repetition steps</div>
              <Button tone="ghost" onClick={() => addSubStep(idx)}>+ Add sub-step</Button>
              <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                {(s.repetitionSteps || []).map((sub, subIdx) => (
                  <Card key={subIdx} title={`Sub-step ${subIdx + 1}`} action={<Button tone="ghost" onClick={() => removeSubStep(idx, subIdx)}>Remove</Button>}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 6 }}>
                      <Input label="Description" value={sub.description || ""} onChange={(v) => updateSubStep(idx, subIdx, "description", v)} />
                      <Input label="Distance (km)" value={sub.distanceKm} onChange={(v) => updateSubStep(idx, subIdx, "distanceKm", Number(v) || 0)} />
                      <Input label="Pace Target" value={sub.paceTarget || ""} onChange={(v) => updateSubStep(idx, subIdx, "paceTarget", v)} />
                      <Input label="Type" value={sub.type || ""} onChange={(v) => updateSubStep(idx, subIdx, "type", v)} />
                      <Select label="Step Type" value={sub.stepType || ""} onChange={(v) => updateSubStep(idx, subIdx, "stepType", v)} options={stepTypeOptions} />
                      <Select label="Duration Type" value={sub.durationType || ""} onChange={(v) => updateSubStep(idx, subIdx, "durationType", v)} options={durationTypeOptions} />
                      <Input label="Duration Value" value={sub.durationValue ?? ""} onChange={(v) => updateSubStep(idx, subIdx, "durationValue", v === "" ? null : Number(v) || 0)} />
                      <Select label="Target Type" value={sub.targetType || ""} onChange={(v) => updateSubStep(idx, subIdx, "targetType", v)} options={targetTypeOptions} />
                      <Input label="Target Low" value={sub.targetLow || ""} onChange={(v) => updateSubStep(idx, subIdx, "targetLow", v)} />
                      <Input label="Target High" value={sub.targetHigh || ""} onChange={(v) => updateSubStep(idx, subIdx, "targetHigh", v)} />
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}
      <Button onClick={addStep}>+ Add step</Button>
    </div>
  );

  return (
    <>
      <TwoColumn
        left={
          showForm ? (
            <Card
              title={workout.workoutId ? "Edit global workout" : "Create global workout"}
              action={<Button tone="ghost" onClick={() => setShowForm(false)}>← Back</Button>}
            >
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px,1fr))", gap: 10 }}>
                {workout.workoutId ? (
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Workout ID</div>
                    <div style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(15,23,42,0.06)", fontFamily: "monospace", fontWeight: 700 }}>
                      {workout.workoutId}
                    </div>
                  </div>
                ) : (
                  <Input label="Workout ID" value={workout.workoutId} onChange={(v) => setWorkout({ ...workout, workoutId: v })} />
                )}
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Version</div>
                  <div style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(15,23,42,0.06)", fontFamily: "monospace", fontWeight: 700 }}>
                    {workout.version}
                  </div>
                </div>
                <Input label="Name" value={workout.name} onChange={(v) => setWorkout({ ...workout, name: v })} />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Type</div>
                  <select
                    value={workout.type}
                    onChange={(e) => setWorkout({ ...workout, type: e.target.value })}
                    style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc", background: "white" }}
                  >
                    <option value="">Select type</option>
                    {typeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <Input label="Intensity" value={workout.intensity} onChange={(v) => setWorkout({ ...workout, intensity: Number(v) || 0 })} />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Tags</div>
                  <Input
                    label=""
                    value={tagInput}
                    onChange={setTagInput}
                    placeholder="Type a tag and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                  />
                  <Button tone="ghost" onClick={addTag}>Add tag</Button>
                  {renderTagPills()}
                </div>
              </div>
              {renderSteps()}
              <TextArea label="Notes" value={workout.notes} onChange={(v) => setWorkout({ ...workout, notes: v })} />
              <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                {!workout.workoutId && <Button onClick={createWorkout}>Create</Button>}
                {workout.workoutId && (
                  <Button tone="secondary" onClick={updateWorkout}>
                    Update
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <Card
              title="Search workouts"
              action={
                <div style={{ display: "flex", gap: 8 }}>
                  <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>
                    {showSearch ? "Hide search" : "Show search"}
                  </Button>
                  <Button onClick={() => { loadWorkout(blankWorkout); setShowForm(true); }}>+ Create</Button>
                </div>
              }
            >
              {showSearch && (
                <Card title="Lookup / versions">
                  <Input label="Workout ID" value={lookupId} onChange={setLookupId} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <Button full onClick={fetchLatest} loading={searching}>Latest</Button>
                    <Button
                      full
                      tone="secondary"
                      onClick={() =>
                        globalWorkoutApi
                          .versions(lookupId)
                          .then((res) => {
                            setResponse(res.data);
                            setPanelState({ results: Array.isArray(res.data) ? res.data : [], showSearch: false });
                          })
                          .catch((e) => setResponse({ error: e.message }))
                      }
                      loading={searching}
                    >
                      Versions list
                    </Button>
                  </div>
                  <Input label="Version" value={version} onChange={setVersion} />
                  <Button full onClick={fetchVersion} loading={searching}>Fetch version</Button>
                </Card>
              )}
              {showSearch && (
                <Card title="Browse by tag">
                  <Input label="Tag" value={tag} onChange={setTag} />
                  <Button full onClick={fetchByTag} loading={searching}>Search</Button>
                </Card>
              )}
              {results.length > 0 && (
                <Card title="Cached results">
                  <div style={{ display: "grid", gap: 6 }}>
                    {results.map((r) => (
                      <div
                        key={`${r.workoutId}-${r.version || "latest"}`}
                        style={{ padding: 10, borderRadius: 10, border: "1px solid rgba(15,23,42,0.08)", cursor: "pointer" }}
                        onClick={() => { loadWorkout(r); setShowForm(true); }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700 }}>{r.name || r.workoutId}</div>
                            <div style={{ fontSize: 12, color: "var(--stone-700)" }}>
                              {r.workoutId} {r.version ? `(v${r.version})` : ""}
                            </div>
                          </div>
                          <Button tone="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(r.workoutId); }}>✕</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </Card>
          )
        }
        right={<div />}
      />
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </>
  );
}
